// Watch page: Player + StreamInfoBar (left) and ChatPanel (right / drawer on mobile).
// The WS socket is opened with the canonical stream.uuid (never the slug), and its status
// frame is preferred over REST once received to drive live/paused/ended transitions.
import { useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useStream } from "../api/hooks.js";
import type { PlaybackEndpoint } from "../api/types.js";
import { ChatPanel } from "../components/ChatPanel.js";
import { StreamInfoBar } from "../components/StreamInfoBar.js";
import { Player } from "../player/Player.js";
import { useStreamSocket } from "../ws/useStreamSocket.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs.js";
import { pageVariants } from "@/lib/motion";

export function StreamPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: stream, isLoading, isError } = useStream(id);

  // Socket is keyed strictly by uuid; only open once we have resolved the stream.
  const sock = useStreamSocket(stream?.uuid);

  const [mobileTab, setMobileTab] = useState<"video" | "chat">("video");

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-4">
        <Skeleton className="aspect-video w-full rounded-2xl" />
        <Skeleton className="mt-4 h-28 w-full rounded-2xl" />
      </div>
    );
  }

  if (isError || !stream) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-20 text-center">
        <h1 className="font-display text-xl font-semibold text-foreground">{t("stream.notFound")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("stream.notFoundHint")}</p>
      </div>
    );
  }

  // Prefer the live socket status frame once received; fall back to the REST snapshot.
  const liveStatus = sock.status?.state ?? stream.status;
  const playbackEndpoints: PlaybackEndpoint[] =
    sock.status?.playbackEndpoints ?? stream.playbackEndpoints;
  const scheduledStartAt =
    sock.status?.scheduledStartAt != null
      ? new Date(sock.status.scheduledStartAt).toISOString()
      : stream.scheduledStartAt;

  // Merge socket-driven status/viewers into the detail object passed to the info bar.
  const startedAtISO =
    sock.status?.startedAt != null ? new Date(sock.status.startedAt).toISOString() : stream.startedAt;
  const mergedStream = { ...stream, status: liveStatus, startedAt: startedAtISO };
  const viewers = sock.viewers || stream.viewerCount;

  return (
    <motion.main
      variants={pageVariants}
      initial="initial"
      animate="enter"
      className="mx-auto max-w-7xl px-4 py-4"
    >
      {/* Mobile tab switcher */}
      <div className="mb-3 lg:hidden">
        <Tabs value={mobileTab} onValueChange={(v) => setMobileTab(v as "video" | "chat")}>
          <TabsList className="w-full">
            <TabsTrigger value="video" className="flex-1">
              {t("stream.video")}
            </TabsTrigger>
            <TabsTrigger value="chat" className="flex-1">
              {t("stream.chat")}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row">
        <div className={`min-w-0 flex-1 ${mobileTab === "chat" ? "hidden lg:block" : ""}`}>
          <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-black shadow-panel">
            <Player
              status={liveStatus}
              playbackEndpoints={playbackEndpoints}
              scheduledStartAt={scheduledStartAt}
              recordings={stream.recordings}
              messages={sock.messages}
              sendChat={sock.sendChat}
            />
          </div>
          <div className="mt-4">
            <StreamInfoBar stream={mergedStream} viewers={viewers} />
          </div>
        </div>

        <div
          className={`lg:w-[348px] lg:shrink-0 ${mobileTab === "video" ? "hidden lg:block" : ""}`}
        >
          <div className="h-[70vh] lg:h-[calc(100vh-7rem)]">
            <ChatPanel
              messages={sock.messages}
              conn={sock.conn}
              sysNotice={sock.sysNotice}
              sendChat={sock.sendChat}
              fetchOlder={sock.fetchOlder}
            />
          </div>
        </div>
      </div>
    </motion.main>
  );
}
